import { useEffect, useState, useMemo } from "react";
import { getobj, postobj, updateobj } from "../apicall/apicall";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Box,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  StyledTableCell,
  StyledTableRow,
  crossHubAction,
} from "../style/style";
import { useLocation } from "react-router-dom";
import YearlyBillingStat from "./YearlyBillingStat";
import MonthlyBillingStat from "./MonthlyBillingStat";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";

const billingColumns = [
  {
    key: "bluetooth",
    label: "Bluetooth Device",
    activeKey: "bluetooth_active_devices",
    billableKey: "bluetooth_billable_devices",
  },
  {
    key: "cellular",
    label: "Cellular Device",
    activeKey: "cellular_active_devices",
    billableKey: "cellular_billable_devices",
  },
];

export default function OrganisationBilling({ id, customerDetails }) {
  const [bills, setBills] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState({ id: "", status: "" });
  const [open, setOpen] = useState(false);
  const [stat, setStat] = useState(false);
  const [data, setData] = useState(null);
  const [isExport, setIsExport] = useState({});
  const [alignment, setAlignment] = useState("month");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () =>
      Array.from({ length: currentYear - 2020 + 1 }, (_, i) => currentYear - i),
    [currentYear],
  );

  useEffect(() => {
    // Set default selected year to current year if not already set
    if (alignment === "year" && !selectedYear) {
      setSelectedYear(currentYear);
      fetchYearlyBills(page, currentYear);
    }
    // eslint-disable-next-line
  }, [alignment]);

  const location = useLocation();

  // Handle alignment toggle (Month/Year)
  const handleAlignmentChange = (event, newAlignment) => {
    if (!newAlignment || newAlignment === alignment) return; // Prevents setting alignment to null
    setAlignment(newAlignment);
    if (newAlignment === "year") {
      fetchYearlyBills(page, selectedYear);
    } else if (newAlignment === "month") {
      fetchBills(page, selectedYear); // Fetch monthly bills
    }
  };

  // Handle page change for pagination
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchBills(value, selectedYear);
  };

  // Fetch bills based on page
  const fetchBills = (page, year) => {
    setLoaded(true);
    const url = `${process.env.REACT_APP_ZEWA_URL}user/invoice/${id}/Monthly/?page=${page}&year=${year}`;
    getobj(url)
      .then((res) => {
        if (res && res.count !== undefined && res.results) {
          setTotalPages(Math.ceil(res.count / 12));
          setBills(res.results);
        } else {
          console.error("Invalid response structure:", res);
          setBills([]);
          setTotalPages(0);
        }
        setLoaded(false);
      })
      .catch((err) => {
        console.error("Error fetching bills:", err);
        setBills([]);
        setTotalPages(0);
        setLoaded(false);
      });
  };

  // Add a new function to fetch yearly bills
  const fetchYearlyBills = (page, year) => {
    setLoaded(true);
    const url = `${process.env.REACT_APP_ZEWA_URL}user/invoice/${id}/Yearly/?page=${page}&year=${year}`;
    getobj(url)
      .then((res) => {
        if (res && res.count !== undefined && res.results) {
          setTotalPages(Math.ceil(res.count / 12));
          setBills(res.results);
        } else {
          console.error("Invalid response structure:", res);
          setBills([]);
          setTotalPages(0);
        }
        setLoaded(false);
      })
      .catch((err) => {
        console.error("Error fetching yearly bills:", err);
        setBills([]);
        setTotalPages(0);
        setLoaded(false);
      });
  };

  // Update billing status
  const updateBill = () => {
    const url = `${process.env.REACT_APP_ZEWA_URL}user/invoice/${id}/?bill=${status.id}`;
    updateobj(url, { status: status.status }).then(() => {
      setOpen(false);
      setRefresh(!refresh);
    });
  };

  // Handle statistics dialog
  const handleStatDialog = (id, date, param) => {
    let bill_type = null;
    let device_type = null;

    // Only enter “year” logic if alignment is "year" *and* we actually have a param
    if (alignment === "year" && param) {
      // decide bill_type based on whether the word "active" or "billable" appears
      if (param.includes("active")) {
        bill_type = "active";
      } else if (param.includes("billable")) {
        bill_type = "billable";
      }

      // decide device_type based on whether the word "bluetooth" or "cellular" appears
      if (param.includes("bluetooth")) {
        device_type = "bluetooth";
      } else if (param.includes("cellular")) {
        device_type = "cellular";
      }
    } else {
      // non-year alignment: only care about device type
      if (param && param.includes("bluetooth")) {
        device_type = "bluetooth";
      } else if (param && param.includes("cellular")) {
        device_type = "cellular";
      } else {
        device_type = "hub";
      }
    }
    setStat(true);
    setData({
      id,
      date,
      data: device_type,
      bill_type,
    });
  };

  // Handle CSV download
  const handleDownload = (startDate, bill_id) => {
    startDate = new Date(startDate);
    startDate.setDate(startDate.getDate() + 2);
    console.log("Download CSV for billId:", bill_id, startDate);
    setIsExport((prev) => ({ ...prev, [bill_id]: true }));
    const url = `${process.env.REACT_APP_ZEWA_URL}user/download-csv/`;
    const data = { id, bill_id, company_name: customerDetails?.company_name };

    postobj(url, data)
      .then((response) => {
        const newObj = { ...isExport };
        delete newObj[bill_id];
        setIsExport(newObj);

        const csvContent = response.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const urlBlob = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = `${customerDetails?.company_name}_${new Date(
          startDate,
        ).toLocaleString("en-us", {
          month: "short",
          year: "numeric",
        })}`;
        link.download = link.download.replace(/ /g, "_");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        NotificationManager.success(
          "Billing devices details exported successfully",
          "Success",
          2000,
        );
      })
      .catch((err) => {
        console.error("Error exporting CSV:", err);
        NotificationManager.error("Error exporting CSV", "Error", 2000);
      });
  };

  // Add a function to handle year selection
  const handleYearChange = (event) => {
    console.log("Year selected:", event.target.value);
    const year = event.target.value;
    setSelectedYear(year);

    if (alignment === "year") {
      fetchYearlyBills(page, year); // Fetch bills for the selected year
    } else {
      fetchBills(page, year);
    }
  };

  // function BillDate({ fromUtc }) {
  //   // parse the ISO string into a Date object
  //   const date = new Date(fromUtc);

  //   // set up your formatting options
  //   const options = {
  //     month: "short", // “Jan”, “Feb”, “Mar”, …
  //     day: "numeric", // “1”, “2”, “3”, …
  //     year: "numeric", // “2025”, …
  //     hour: "numeric", // “5”, “11”, …
  //     minute: "numeric", // “30”, “05”, …
  //     hour12: true, // 12-hour clock + AM/PM
  //   };

  //   // choose the locale you want; e.g. “en-US”
  //   const formatted = new Intl.DateTimeFormat("en-US", options).format(date);

  //   return <span>{formatted}</span>;
  // }

  useEffect(() => {
    fetchBills(page, currentYear);
  }, [refresh, page, currentYear]);

  return (
    <div>
      {loaded ? (
        <img
          src="https://zewa-logo-bucket.s3.amazonaws.com/logo/Spinner-1s-200px.gif"
          alt="Loading..."
        />
      ) : (
        <TableContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "16px",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <Box>
              <Box>
                <TextField
                  select
                  label="Year"
                  value={selectedYear || currentYear}
                  onChange={handleYearChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Select Year" }}
                  sx={{ minWidth: "clamp(80px, 12vw, 100px)" }}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
            <Box sx={{ height: "100%" }}>
              <ToggleButtonGroup
                color="primary"
                value={alignment}
                exclusive
                onChange={handleAlignmentChange}
                aria-label="Billing Period"
                sx={{ height: "56px" }}
              >
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          {bills && bills.length > 0 ? (
            <Table aria-label="Billing Table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    <b>Month</b>
                  </StyledTableCell>
                  {billingColumns.map((col) => (
                    <StyledTableCell key={col.key}>
                      {alignment === "year" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ fontWeight: "bold" }}>{col.label}</Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 14,
                              fontWeight: "normal",
                              width: "48%",
                            }}
                          >
                            <span>Active</span>
                            <span>Billable</span>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <b>{col.label}</b>
                        </Box>
                      )}
                    </StyledTableCell>
                  ))}
                  {alignment === "month" && (
                    <StyledTableCell>
                      <b>Hubs</b>
                    </StyledTableCell>
                  )}
                  {location.pathname === "/admin" &&
                    customerDetails &&
                    alignment === "year" && <StyledTableCell></StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {bills &&
                  bills.map((bill) => (
                    <StyledTableRow key={bill.id}>
                      <StyledTableCell>
                        {new Date(bill.to_utc).toLocaleString("en-us", {
                          month: "short",
                        })}
                      </StyledTableCell>
                      {billingColumns.map((col) => (
                        <StyledTableCell
                          key={col.key}
                          sx={{ textAlign: "center" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent:
                                alignment === "month"
                                  ? "center"
                                  : "space-evenly",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            {alignment === "year" ? (
                              <>
                                <Link
                                  onClick={() =>
                                    handleStatDialog(
                                      bill.id,
                                      {
                                        from_utc: bill.from_utc,
                                        to_utc: bill.to_utc,
                                      },
                                      col.activeKey,
                                    )
                                  }
                                >
                                  {bill[col.activeKey]}
                                </Link>
                                <Link
                                  onClick={() =>
                                    handleStatDialog(
                                      bill.id,
                                      {
                                        from_utc: bill.from_utc,
                                        to_utc: bill.to_utc,
                                      },
                                      col.billableKey,
                                    )
                                  }
                                >
                                  {bill[col.billableKey]}
                                </Link>
                              </>
                            ) : (
                              <Link
                                onClick={() =>
                                  handleStatDialog(
                                    bill.id,
                                    {
                                      from_utc: bill.from_utc,
                                      to_utc: bill.to_utc,
                                    },
                                    col.key + "_device",
                                  )
                                }
                              >
                                {bill[col.key + "_device"]}
                              </Link>
                            )}
                          </Box>
                        </StyledTableCell>
                      ))}
                      {alignment === "month" && (
                        <StyledTableCell>
                          <Link
                            onClick={() =>
                              handleStatDialog(
                                bill.id,
                                {
                                  from_utc: bill.from_utc,
                                  to_utc: bill.to_utc,
                                },
                                "hub",
                              )
                            }
                          >
                            {bill.hubs}
                          </Link>
                        </StyledTableCell>
                      )}
                      {location.pathname === "/admin" && (
                        <StyledTableCell>
                          <LoadingButton
                            size="small"
                            onClick={() =>
                              handleDownload(bill.from_utc, bill.id)
                            }
                            endIcon={<RefreshIcon />}
                            loading={isExport[bill.id]}
                            loadingPosition="end"
                            variant="contained"
                            disabled={
                              Object.keys(isExport).length && !isExport[bill.id]
                            }
                          >
                            <span>Export</span>
                          </LoadingButton>
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            <div style={{ padding: "20px", textAlign: "center" }}>
              No customer billing data available for the selected period.
            </div>
          )}
          <NotificationContainer />
        </TableContainer>
      )}
      <Pagination
        count={totalPages}
        page={page}
        variant="outlined"
        color="primary"
        onChange={handlePageChange}
      />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Update Billing Status</DialogTitle>
        <TextField
          select
          label="Select Billing Status"
          name="status"
          sx={{ width: "100%", maxWidth: "400px", margin: "0.3125rem" }}
          variant="outlined"
          onChange={(e) => setStatus({ ...status, status: e.target.value })}
        >
          <MenuItem value={true}>Paid</MenuItem>
          <MenuItem value={false}>Unpaid</MenuItem>
        </TextField>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={updateBill}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={stat} maxWidth="xl" onClose={() => setStat(false)}>
        <DialogContent style={{ overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <CloseIcon sx={crossHubAction} onClick={() => setStat(false)} />
          </div>
          {alignment === "year" ? (
            <YearlyBillingStat data={data} customerId={id} />
          ) : (
            <MonthlyBillingStat data={data} customerId={id} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
